import React, { useCallback, useEffect, useState } from "react";
import { TrendingUp, Wallet, ShoppingBag, Trophy, Users, Flame, FileDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { formatGNF, imgUrl } from "@/utils/format";
import { KpiCard, OrderHeatmap, RangeSelector, fmtNumber } from "@/components/dashboard/DashboardWidgets";
import ExportButtons from "@/components/dashboard/ExportButtons";
import { downloadCsv, downloadPdf, slugify, todayStamp } from "@/utils/exportHelpers";

/**
 * Shared restaurant dashboard widget.
 * @param {string} [restaurantId] – when provided (admin view), fetch that restaurant.
 *   Otherwise the backend infers the current user's restaurant.
 */
export default function RestaurantDashboardWidget({ restaurantId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [statementMonth, setStatementMonth] = useState(() => new Date().toISOString().slice(0, 7));

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ days: String(days) });
    if (restaurantId) qs.set("restaurant_id", restaurantId);
    apiClient.get(`/dashboard/restaurant?${qs.toString()}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [days, restaurantId]);

  const restName = data?.restaurant?.name || "restaurant";
  const filenameBase = `neldaari-${slugify(restName)}-${days}j-${todayStamp()}`;
  // Vue admin (restaurantId fourni) : le taux de commission reste visible en permanence.
  // Vue partenaire : "Frais de service" sans pourcentage (pratique Uber Eats / Deliveroo).
  const isAdminView = Boolean(restaurantId);

  const downloadStatement = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await apiClient.get(`/my/restaurant/commission-statement?month=${statementMonth}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `neldaari_detail_${statementMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossible de générer le document — réessayez.");
    } finally {
      setDownloading(false);
    }
  }, [statementMonth]);

  const buildCsv = useCallback(() => {
    if (!data) return;
    const k = data.kpis || {};
    downloadCsv(`${filenameBase}.csv`, [
      {
        title: `${t("dashboard.restaurantTitle")} — ${restName}`,
        headers: [t("dashboard.pdfPeriod"), `${days}j`],
        rows: [],
      },
      {
        title: t("dashboard.pdfSection.kpis"),
        headers: [t("dashboard.revenue"), t("dashboard.orders"), t("dashboard.aov"), t("dashboard.deliveredCancelled")],
        rows: [[k.revenue ?? 0, k.orders ?? 0, k.aov ?? 0, `${k.delivered ?? 0} / ${k.cancelled ?? 0}`]],
      },
      {
        title: t("dashboard.pdfSection.series"),
        headers: ["date", t("dashboard.revenue"), t("dashboard.orders")],
        rows: (data.series || []).map((s) => [s.date, s.revenue ?? 0, s.orders ?? 0]),
      },
      {
        title: t("dashboard.pdfSection.topDishes"),
        headers: ["#", t("dashboard.pdfSection.topDishes"), t("dashboard.orders"), t("dashboard.revenue")],
        rows: (data.top_dishes || []).map((d, i) => [i + 1, d.name, d.quantity, d.revenue]),
      },
      {
        title: t("dashboard.pdfSection.topClients"),
        headers: ["#", t("dashboard.selectDriver") /* client name */, t("dashboard.orders"), "Total"],
        rows: (data.top_clients || []).map((c, i) => [i + 1, c.name, c.orders_count, c.total_spent]),
      },
    ]);
  }, [data, days, filenameBase, restName, t]);

  const buildPdf = useCallback(() => {
    if (!data) return;
    const k = data.kpis || {};
    const now = new Date();
    downloadPdf(`${filenameBase}.pdf`, {
      title: `${t("dashboard.restaurantTitle")} — ${restName}`,
      subtitle: t("dashboard.restaurantSubtitle"),
      period: `${t("dashboard.pdfPeriod")}: ${days}${lang === "en" ? "d" : "j"}`,
      generatedLabel: `${t("dashboard.pdfGeneratedOn")} ${now.toLocaleString(lang === "en" ? "en-US" : "fr-FR")}`,
      sections: [
        {
          title: t("dashboard.pdfSection.kpis"),
          kvs: [
            [t("dashboard.revenue"), formatGNF(k.revenue)],
            [t("dashboard.orders"), fmtNumber(k.orders, lang)],
            [t("dashboard.aov"), formatGNF(k.aov)],
            [t("dashboard.deliveredCancelled"), `${k.delivered ?? 0} / ${k.cancelled ?? 0}`],
          ],
        },
        {
          title: t("dashboard.pdfSection.topDishes"),
          headers: ["#", t("dashboard.pdfSection.topDishes"), t("dashboard.orders"), t("dashboard.revenue")],
          rows: (data.top_dishes || []).map((d, i) => [i + 1, d.name, d.quantity, formatGNF(d.revenue)]),
        },
        {
          title: t("dashboard.pdfSection.topClients"),
          headers: ["#", "Client", t("dashboard.orders"), "Total"],
          rows: (data.top_clients || []).map((c, i) => [i + 1, c.name, c.orders_count, formatGNF(c.total_spent)]),
        },
        {
          title: t("dashboard.pdfSection.series"),
          headers: ["date", t("dashboard.revenue"), t("dashboard.orders")],
          rows: (data.series || []).map((s) => [s.date, formatGNF(s.revenue), s.orders ?? 0]),
        },
      ],
    });
  }, [data, days, filenameBase, lang, restName, t]);

  if (loading || !data) return <div className="text-mute text-sm py-10">{t("dashboard.loading")}</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="restaurant-dashboard-widget">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-red">{data.restaurant?.name}</div>
          <h2 className="font-display text-2xl font-semibold mt-1">{t("dashboard.performanceTitle", { n: days })}</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButtons onExportPdf={buildPdf} onExportCsv={buildCsv} testidPrefix="rest" />
          <RangeSelector value={days} onChange={setDays} testidPrefix="rest-range" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={<TrendingUp className="w-4 h-4" />} label={t("rdash.caBrut")} value={formatGNF(k.ca_brut_plats ?? k.revenue)} growthPct={k.growth.revenue_pct} color="#E32636" testid="kpi-ca-brut" />
        <KpiCard icon={<ShoppingBag className="w-4 h-4" />} label={t("dashboard.orders")} value={fmtNumber(k.orders, lang)} growthPct={k.growth.orders_pct} color="#F4C430" testid="kpi-orders" />
        <KpiCard icon={<Wallet className="w-4 h-4" />} label={t("rdash.panierMoyen")} value={formatGNF(k.aov)} growthPct={k.growth.aov_pct} color="#009E60" testid="kpi-aov" />
        <KpiCard icon={<Flame className="w-4 h-4" />} label={t("rdash.deliveredCancelled")} value={`${k.delivered} / ${k.cancelled}`} color="#1A1A1A" testid="kpi-delivered" />
      </div>

      {/* Compta breakdown */}
      <div className="bg-white border border-sand rounded-2xl p-5" data-testid="kpi-compta-breakdown">
        <h3 className="font-display text-lg font-semibold mb-1">Comptabilité — vue partenaire</h3>
        <p className="text-xs text-mute mb-4">
          Votre chiffre d'affaires est calculé uniquement sur le prix de vos plats (suppléments inclus).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl bg-[#FFF5F6] border border-brand-red/20 p-4" data-testid="compta-ca-brut">
            <div className="text-[11px] uppercase tracking-widest text-mute font-semibold">{t("rdash.caBrut")}</div>
            <div className="font-display text-2xl font-bold text-brand-red mt-1">{formatGNF(k.ca_brut_plats ?? k.revenue)}</div>
            <div className="text-xs text-mute mt-1">{t("rdash.caBrutSub")}</div>
          </div>
          <div className="rounded-xl bg-elevated border border-sand p-4" data-testid="compta-commission">
            <div className="text-[11px] uppercase tracking-widest text-mute font-semibold">
              {isAdminView ? `${t("rdash.commissionNel")} (${k.commission_rate_pct}%)` : t("rdash.serviceFees")}
            </div>
            <div className="font-display text-2xl font-bold text-ink mt-1">− {formatGNF(k.commission)}</div>
            <div className="text-xs text-mute mt-1">
              {isAdminView
                ? (data.restaurant?.livraison_propre ? t("rdash.rateOwnDelivery") : t("rdash.rateStandard"))
                : t("rdash.feeDetailHint")}
            </div>
          </div>
          <div className="rounded-xl bg-[#E7F6EE] border border-brand-green/30 p-4" data-testid="compta-net">
            <div className="text-[11px] uppercase tracking-widest text-brand-green font-semibold">{isAdminView ? t("rdash.netToRemit") : t("rdash.netToReceive")}</div>
            <div className="font-display text-2xl font-bold text-brand-green mt-1">{formatGNF(k.net_a_reverser)}</div>
            <div className="text-xs text-mute mt-1">{isAdminView ? t("rdash.caMinusCommission") : t("rdash.caMinusFees")}</div>
          </div>
        </div>
        {!isAdminView && (
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="compta-statement-row">
            <select
              value={statementMonth}
              onChange={(e) => setStatementMonth(e.target.value)}
              data-testid="statement-month-select"
              className="bg-white border border-sand rounded-xl px-3 py-2 text-sm outline-none focus:border-brand-red"
            >
              {Array.from({ length: 12 }, (_, i) => {
                const d = new Date();
                d.setDate(1);
                d.setMonth(d.getMonth() - i);
                const v = d.toISOString().slice(0, 7);
                return <option key={v} value={v}>{d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</option>;
              })}
            </select>
            <button
              onClick={downloadStatement}
              disabled={downloading}
              data-testid="statement-download-btn"
              className="inline-flex items-center gap-2 rounded-full bg-ink hover:bg-black disabled:opacity-50 text-white px-4 py-2 text-sm font-semibold"
            >
              <FileDown className="w-4 h-4" />
              {downloading ? t("rdash.generating") : t("rdash.download")}
            </button>
            <span className="text-xs text-mute">{t("rdash.statementHint")}</span>
          </div>
        )}
        {k.incident_count > 0 && (
          <div className="mt-4 rounded-xl bg-[#FFF5F6] border border-brand-red/20 p-3 text-xs text-mute" data-testid="compta-incident-warning">
            ⚠ <b>{k.incident_count}</b> commande(s) en incident non comptabilisée(s) — NelDaari doit arbitrer avant reversement.
          </div>
        )}
      </div>

      {/* Trend chart */}
      <div className="bg-white border border-sand rounded-2xl p-4">
        <h3 className="font-semibold mb-3">{t("rdash.trendTitle")}</h3>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={data.series} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <defs>
                <linearGradient id="restRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E32636" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#E32636" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBE8E0" />
              <XAxis dataKey="date" fontSize={11} tickFormatter={(d) => d.slice(5)} />
              <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v, name) => name === "revenue" ? formatGNF(v) : v} />
              <Area type="monotone" dataKey="revenue" name={t("dashboard.revenue")} stroke="#E32636" fill="url(#restRev)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top 5 dishes */}
      <div className="bg-white border border-sand rounded-2xl p-4" data-testid="top-dishes">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-brand-red" /> {t("dashboard.topDishes")}</h3>
        {data.top_dishes.length === 0 ? (
          <div className="text-mute text-sm">{t("dashboard.noSales")}</div>
        ) : (
          <div className="space-y-2">
            {data.top_dishes.map((d, i) => (
              <div key={d.dish_id} data-testid={`top-dish-${i}`} className="flex items-center gap-3 py-1">
                <span className="font-display text-lg text-mute w-5">{i + 1}</span>
                <img src={imgUrl(d.image)} alt={d.name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{d.name}</div>
                  <div className="text-xs text-mute">{d.quantity} · {formatGNF(d.revenue)}</div>
                </div>
                {d.growth_pct != null && (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${d.growth_pct >= 0 ? "bg-[#E7F6EE] text-brand-green" : "bg-[#FFF5F6] text-brand-red"}`}>
                    {d.growth_pct >= 0 ? "+" : ""}{d.growth_pct}%
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Heatmap + top clients side by side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-semibold mb-3">{t("dashboard.heatmap")}</h3>
          <OrderHeatmap data={data.heatmap} lang={lang} />
        </div>
        <div className="bg-white border border-sand rounded-2xl p-4" data-testid="top-clients">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-brand-red" /> {t("dashboard.topClients")}</h3>
          {data.top_clients.length === 0 ? (
            <div className="text-mute text-sm">{t("dashboard.noClients")}</div>
          ) : (
            <ol className="space-y-1.5">
              {data.top_clients.map((c, i) => (
                <li key={c.user_id} className="flex items-center justify-between text-sm py-1 border-b border-sand/60 last:border-0" data-testid={`top-client-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-mute w-5 text-xs">{i + 1}</span>
                    <span className="font-medium truncate">{c.name}</span>
                    <span className="text-xs text-mute shrink-0">· {c.orders_count}</span>
                  </div>
                  <span className="font-semibold text-brand-green shrink-0 ml-2">{formatGNF(c.total_spent)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
